import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { InfoRow } from '../components/servers/ServerWidgets';
import {
  BackLink,
  FieldChanges,
  HitsTable,
  IdentityCard,
  Section,
  VerdictBanner,
  backendLabel,
  eventSummary,
  formatWhen,
} from '../components/security/SecurityDetail';
import { eventTypeLabel, severityLabel, sourceLabel } from '../components/security/securityLabels';
import { apiGetCybersecurityEventDetail } from '../api/client';

export default function SecurityEventDetailPage() {
  const { source, id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await apiGetCybersecurityEventDetail(source, id);
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load event');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [source, id]);

  const event = data?.event;
  const payload = event?.payload || {};

  return (
    <AppLayout>
      <div className="page-content">
        <BackLink />
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        {loading && <p style={{ color: '#64748b' }}>Loading event…</p>}
        {!loading && event && (
          <>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: '12px 0 4px' }}>{eventTypeLabel(event.type)}</h1>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              {backendLabel(source)} · {severityLabel(event.severity)} · {formatWhen(event.created_at)}
            </p>
            <VerdictBanner
              level={event.severity === 'critical' ? 'critical' : event.severity === 'warning' ? 'warning' : 'info'}
              text={eventSummary(event)}
            />

            <IdentityCard user={data.user} />

            {event.type === 'registry_identity_change' && (
              <Section title="Old vs new">
                <p style={{ margin: '0 0 10px', color: '#64748b' }}>VIN {payload.vin || '—'}</p>
                <FieldChanges
                  changes={payload.changes}
                  emptyText={
                    (payload.paths || []).length
                      ? `Changed: ${(payload.paths || []).join(', ')}. Old/new was not stored on this older event.`
                      : 'No field diffs recorded.'
                  }
                />
                {payload.registry_update_id && (
                  <button
                    type="button"
                    onClick={() => navigate(`/cybersecurity/registry-update/${payload.registry_update_id}`)}
                    style={{ marginTop: 14, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Open full update
                  </button>
                )}
              </Section>
            )}

            <Section title="What they requested">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                <InfoRow label="What happened" value={eventTypeLabel(event.type)} />
                <InfoRow label="Why" value={payload.source ? sourceLabel(payload.source) : '—'} />
                <InfoRow label="Method + path" value={event.action || `${payload.method || ''} ${payload.path || ''}`} />
                <InfoRow label="HTTP" value={payload.http_status} />
                <InfoRow label="Address" value={event.ip} />
                <InfoRow label="Count" value={event.count} />
                <InfoRow label="User-Agent" value={payload.userAgent} />
                <InfoRow label="Session" value={payload.session_id} />
                <InfoRow label="Device" value={payload.device_id} />
                <InfoRow label="Login hint" value={payload.login_hint} />
                <InfoRow label="Who" value={event.actor_key} />
                <InfoRow label="Notified" value={event.notified_at ? formatWhen(event.notified_at) : 'Not yet'} />
              </div>
              {event.ip && (
                <button
                  type="button"
                  onClick={() => navigate(`/cybersecurity/ip/${source}/${encodeURIComponent(event.ip)}`)}
                  style={{ marginTop: 14, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontWeight: 700, cursor: 'pointer' }}
                >
                  Open address {event.ip}
                </button>
              )}
            </Section>

            <Section title="Nearby hits on this IP / actor">
              <HitsTable hits={data.hits} source={source} />
            </Section>
          </>
        )}
      </div>
    </AppLayout>
  );
}
